import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FormSectionElements, ProgramContainer, LOVValues } from '../../entities/formTemplateDataType';
import { getRootComponent } from '../../utilities/ElementUtils';
import axios from 'axios';
import SessionStorageService from '../../services/SessionStorageService';
import { setBaseHeaders } from '../../utilities/submitUtils';
import { ElementComponent } from '../ElementComponent';
import { User } from '../../entities/User';
import {ReactComponent as ChevronDown} from '/public/assets/css/ontario-design-system/icons/svg/ontario-icon-chevron-down.svg';
import {ReactComponent as ChevronUp} from '/public/assets/css/ontario-design-system/icons/svg/ontario-icon-chevron-up.svg';
import { resolveApiUrl } from '../../utilities/URLUtils';
import { DynamicUrlInterpreter } from '../../utilities/DynamicUrlInterpreter';
import './PagedSummaryListComponent.css';

const DEFAULT_PAGE_SIZE = 10;

export type DynamicAccordionFormProps = {
    isEng: boolean;
    field: FormSectionElements;
    callbackStepIndex?: (index: number, returnIndex?: number) => void;
    callback?: (programContainer: ProgramContainer) => void;
    user?: User;
    editable?: boolean;
};

type AccordionItem = {
    title: string;
    expand: boolean;
    data: any;
    entries: any[];
    accordionIndex: number;
};

const DynamicAccordionFormComponent = ({
    field,
    isEng,
    callbackStepIndex,
    callback,
    user,
    editable = true
}: DynamicAccordionFormProps) => {
    const [accordionItems, setAccordionItems] = useState<AccordionItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [expandedAccordions, setExpandedAccordions] = useState<Set<number>>(new Set());
    const [formElementsCache, setFormElementsCache] = useState<Map<string, FormSectionElements[]>>(new Map());
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLastPage, setIsLastPage] = useState<boolean>(false);

    const defaultConfig = {
        titleField: '',
        entriesField: '',
        formFields: []
    };

    const config = field.DynamicAccordionFormConfig || defaultConfig;

    const pageSize = field.PageSize || DEFAULT_PAGE_SIZE;
    const isClientSide = field.ClientSidePagination === true;
    const isPaginationEnabled = !!field.PageSize;

    const totalPages = isPaginationEnabled
        ? (isClientSide
            ? Math.ceil(accordionItems.length / pageSize)
            : Math.ceil(totalCount / pageSize))
        : 1;

    const parseDateValue = (value: any): string | null => {
        if (!value || value === '') return null;

        const dateStr = String(value).trim();
        if (!dateStr) return null;

        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
            return dateStr;
        }

        const isoMatch = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        if (isoMatch) {
            const [, year, month, day] = isoMatch;
            return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        }

        const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        }

        const yyyyddmmMatch = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
        if (yyyyddmmMatch) {
            const [, year, day, month] = yyyyddmmMatch;
            return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        }

        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj.getTime())) {
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${month}/${day}/${year}`;
        }

        return null;
    };

    const extractDataFromPath = (response: any, path: string): any => {
        if (!path) return response;
        const keys = path.split('.');
        let result = response;
        for (const key of keys) {
            if (result && typeof result === 'object' && key in result) {
                result = result[key];
            } else {
                return null;
            }
        }
        return result;
    };

    const fetchData = useCallback(async (page: number) => {
        const programContainer = getRootComponent(field);
        const currentConfig = field.DynamicAccordionFormConfig;

        if (!field.LovAPI) {
            setIsLoading(false);
            return;
        }

        // Resolve pagination placeholders first, then apply DynamicUrlInterpreter for the rest
        let api = resolveApiUrl(field.LovAPI, { page, pageSize, programContainer });

        api = DynamicUrlInterpreter.interpretUrl(api, {
            programContainer,
            appName: programContainer.CategoryId
        });

        const isExternalUrl = api.startsWith("http");

        if (!isExternalUrl) {
            api = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + api.replace(/^\/+/, '');
        }

        const urlWithoutProtocol = api.replace(/^https?:\/\//, '');
        if (urlWithoutProtocol.includes('//')) {
            console.log('[DynamicAccordionForm] Skipping request with unresolved placeholder:', api);
            setIsLoading(false);
            return;
        }

        const apiKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
            ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
            : null;

        const token = !isExternalUrl
            ? (SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token)
            : null;

        const headers = setBaseHeaders('application/json', token, apiKey);
        const configs = isExternalUrl
            ? { headers: { 'Content-Type': 'application/json' } }
            : { headers, withCredentials: true };

        try {
            setIsLoading(true);
            const response = await axios.get(api, configs);
            const dataPath = field.DataPath || '';
            let extractedData = dataPath ? extractDataFromPath(response.data, dataPath) : response.data;
            let dataArray: any[] = [];

            if (Array.isArray(extractedData)) {
                dataArray = extractedData;
            } else if (extractedData && typeof extractedData === 'object') {
                const allArrayKeys = Object.keys(extractedData).filter(key =>
                    Array.isArray(extractedData[key])
                );

                if (allArrayKeys.length > 0) {
                    dataArray = extractedData[allArrayKeys[0]];
                } else if (currentConfig && currentConfig.titleField && extractedData[currentConfig.titleField]) {
                    dataArray = [extractedData];
                }
            }

            // Read total count for server-side pagination
            if (isPaginationEnabled && !isClientSide) {
                const totalPath = field.TotalCountPath || 'totalCount';
                let total = extractDataFromPath(response.data, totalPath);
                if (total === null || total === undefined) {
                    total = response.data?.totalElements
                        ?? response.data?.total
                        ?? response.data?.totalCount
                        ?? response.data?.count
                        ?? dataArray.length;
                }
                setTotalCount(Number(total) || dataArray.length);

                if (field.IsLastPath) {
                    const isLast = extractDataFromPath(response.data, field.IsLastPath);
                    setIsLastPage(isLast === true);
                }
            }

            if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
                transformToAccordions(dataArray, currentConfig);
            } else {
                setAccordionItems([]);
                field.Value = [];
                (field as any).accordionData = [];
            }
        } catch (error: any) {
            setAccordionItems([]);
            field.Value = [];
            (field as any).accordionData = [];
        } finally {
            setIsLoading(false);
        }
    }, [field.LovAPI, field.DataPath, field.DynamicAccordionFormConfig, field.Id, pageSize, isClientSide, isPaginationEnabled]);

    const createFormElementsForEntry = (
        entry: any,
        entryIndex: number,
        parentItem: any,
        parentIndex: number,
        currentConfig: typeof config
    ): FormSectionElements[] => {
        const formElements: FormSectionElements[] = [];

        currentConfig.formFields.forEach((fieldDef, fieldIndex) => {
            const formElement = new FormSectionElements();
            formElement.Id = `${field.Id}_accordion_${parentIndex}_entry_${entryIndex}_field_${fieldIndex}`;

            if (fieldDef.type === 'ReadOnly') {
                formElement.Datatype = 'Label';
            } else if (fieldDef.type === 'Date') {
                formElement.Datatype = 'ODSDate';
            } else if (fieldDef.type === 'Dropdown') {
                formElement.Datatype = 'Dropdown';
                if (fieldDef.options && Array.isArray(fieldDef.options)) {
                    formElement.ListOfLOVValues = [];
                    fieldDef.options.forEach((option, optionIndex) => {
                        const lov = new LOVValues();
                        lov.id = `${formElement.Id}_option_${optionIndex}`;
                        lov.Sequence = optionIndex + 1;
                        lov.code = option.value || option.code || '';
                        lov.description = option.label || option.description || option.value || option.code || '';
                        lov.FRADescription = option.labelFRA || option.descriptionFRA || lov.description;
                        lov.parent = formElement;
                        lov.Visible = true;
                        formElement.ListOfLOVValues.push(lov);
                    });
                }
            } else if (fieldDef.type === 'MultipleChoice') {
                formElement.Datatype = 'CheckGroup';
                if (fieldDef.options && Array.isArray(fieldDef.options)) {
                    formElement.ListOfLOVValues = [];
                    fieldDef.options.forEach((option, optionIndex) => {
                        const lov = new LOVValues();
                        lov.id = `${formElement.Id}_option_${optionIndex}`;
                        lov.Sequence = optionIndex + 1;
                        lov.code = option.value || option.code || '';
                        lov.description = option.label || option.description || option.value || option.code || '';
                        lov.FRADescription = option.labelFRA || option.descriptionFRA || lov.description;
                        lov.parent = formElement;
                        lov.Visible = true;
                        formElement.ListOfLOVValues.push(lov);
                    });
                }
            } else {
                formElement.Datatype = fieldDef.type;
            }

            formElement.ENUTitle = fieldDef.label;
            formElement.FRATitle = fieldDef.labelFRA || fieldDef.label;

            if (currentConfig.entriesField && entry !== parentItem) {
                formElement.LoadBindName = `${currentConfig.entriesField}[${entryIndex}].${fieldDef.bindName}`;
            } else {
                formElement.LoadBindName = fieldDef.bindName;
            }

            formElement.Editable = (fieldDef.editable !== false && fieldDef.type !== 'ReadOnly') ? 'Y' : 'N';
            formElement.Optional = fieldDef.required ? 'N' : 'Y';
            formElement.Valid = true;
            formElement.Visible = true;
            formElement.Sequence = fieldIndex;

            const rawValue = entry[fieldDef.bindName] ?? '';
            if (fieldDef.type === 'Date' && rawValue) {
                const parsedDate = parseDateValue(rawValue);
                formElement.Value = parsedDate || rawValue;
            } else if (fieldDef.type === 'Dropdown' && rawValue) {
                if (formElement.ListOfLOVValues && formElement.ListOfLOVValues.length > 0) {
                    const matchingOption = formElement.ListOfLOVValues.find(lov =>
                        lov.code === rawValue ||
                        lov.description === rawValue ||
                        lov.code?.toString() === rawValue?.toString()
                    );
                    formElement.Value = matchingOption ? matchingOption.code : '';
                } else {
                    formElement.Value = rawValue;
                }
            } else if (fieldDef.type === 'MultipleChoice' && rawValue) {
                let selectedValues: string[] = [];

                if (Array.isArray(rawValue)) {
                    selectedValues = rawValue.map(v => String(v));
                } else if (typeof rawValue === 'string' && rawValue) {
                    selectedValues = rawValue.includes(';') ? rawValue.split(';') :
                                   rawValue.includes(',') ? rawValue.split(',') :
                                   [rawValue];
                }

                const getDelimiter = () => getRootComponent(field)?.ToSanitize ? '|' : '#';
                const delimiter = getDelimiter();

                if (formElement.ListOfLOVValues && formElement.ListOfLOVValues.length > 0 && selectedValues.length > 0) {
                    const matchedCodes: string[] = [];
                    selectedValues.forEach(value => {
                        const trimmedValue = String(value).trim();
                        const matchingOption = formElement.ListOfLOVValues.find(lov =>
                            lov.code === trimmedValue ||
                            lov.description === trimmedValue ||
                            lov.code?.toString() === trimmedValue
                        );
                        if (matchingOption && !matchedCodes.includes(matchingOption.code)) {
                            matchedCodes.push(matchingOption.code);
                        }
                    });
                    formElement.Value = matchedCodes.length > 0 ? matchedCodes.join(delimiter) : '';
                } else {
                    formElement.Value = selectedValues.join(delimiter);
                }
            } else {
                formElement.Value = rawValue;
            }

            formElement.ReadOnly = fieldDef.type === 'ReadOnly' || fieldDef.editable === false;
            formElement.parent = field.parent;

            formElements.push(formElement);
        });

        return formElements;
    };

    const transformToAccordions = (data: any[], currentConfig: typeof config) => {
        const items: AccordionItem[] = [];

        if (!data || data.length === 0) {
            setAccordionItems([]);
            field.Value = [];
            (field as any).accordionData = [];
            setFormElementsCache(new Map());
            return;
        }

        data.forEach((item, index) => {
            const title = String(item[currentConfig.titleField] || `Item ${index + 1}`);

            let entries: any[] = [];
            if (currentConfig.entriesField && Array.isArray(item[currentConfig.entriesField])) {
                entries = item[currentConfig.entriesField];
            } else {
                entries = [item];
            }

            const completeEntries = entries.map((entry) => {
                const completeEntry = { ...entry };
                if (currentConfig.formFields) {
                    currentConfig.formFields.forEach((fieldDef) => {
                        if (!(fieldDef.bindName in completeEntry)) {
                            const value = entry[fieldDef.bindName] ?? item[fieldDef.bindName] ?? '';
                            completeEntry[fieldDef.bindName] = value;
                        }
                    });
                }
                return completeEntry;
            });

            items.push({
                title: title,
                expand: false,
                data: item,
                entries: completeEntries,
                accordionIndex: index
            });
        });

        setAccordionItems(items);

        const processedItems = items.map((item) => {
            const updatedData = { ...item.data };
            if (currentConfig.entriesField && currentConfig.entriesField in updatedData) {
                updatedData[currentConfig.entriesField] = item.entries;
            } else {
                if (item.entries.length > 0) {
                    Object.assign(updatedData, item.entries[0]);
                }
            }
            return {
                ...item,
                data: updatedData,
                entries: item.entries
            };
        });

        field.Value = processedItems.map(item => ({
            ...item.data,
            entries: item.entries
        }));
        (field as any).accordionData = processedItems;

        setFormElementsCache(new Map());
    };

    const handleFieldChange = (programContainer: ProgramContainer) => {
        const updatedItems = accordionItems.map((item) => {
            const updatedEntries = item.entries.map((entry) => {
                const completeEntry = { ...entry };
                if (config.formFields) {
                    config.formFields.forEach((fieldDef) => {
                        if (!(fieldDef.bindName in completeEntry)) {
                            const value = entry[fieldDef.bindName] ?? item.data[fieldDef.bindName] ?? '';
                            completeEntry[fieldDef.bindName] = value;
                        } else if (fieldDef.type === 'ReadOnly') {
                            if (!completeEntry[fieldDef.bindName] && item.data[fieldDef.bindName]) {
                                completeEntry[fieldDef.bindName] = item.data[fieldDef.bindName];
                            }
                        }
                    });
                }
                return completeEntry;
            });

            const updatedData = { ...item.data };
            if (config.entriesField && config.entriesField in updatedData) {
                updatedData[config.entriesField] = updatedEntries;
            } else {
                if (updatedEntries.length > 0) {
                    Object.assign(updatedData, updatedEntries[0]);
                }
            }

            return {
                ...item,
                data: updatedData,
                entries: updatedEntries
            };
        });
        setAccordionItems(updatedItems);
        field.Value = updatedItems.map(item => ({
            ...item.data,
            entries: item.entries
        }));
        (field as any).accordionData = updatedItems;
        callback?.(programContainer);
    };

    const toggleAccordion = (index: number) => {
        const newExpanded = new Set(expandedAccordions);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedAccordions(newExpanded);

        const updated = accordionItems.map((item) => ({
            ...item,
            expand: newExpanded.has(item.accordionIndex)
        }));
        setAccordionItems(updated);
    };

    const toggleExpandAll = () => {
        const allExpanded = expandedAccordions.size === accordionItems.length;
        const newExpanded = allExpanded
            ? new Set<number>()
            : new Set(accordionItems.map((item) => item.accordionIndex));
        setExpandedAccordions(newExpanded);

        const updated = accordionItems.map((item) => ({
            ...item,
            expand: !allExpanded
        }));
        setAccordionItems(updated);
    };

    const handlePageChange = (newPage: number) => {
        const upperBound = field.IsLastPath ? true : newPage < totalPages;
        if (newPage >= 0 && upperBound) {
            setCurrentPage(newPage);
            setExpandedAccordions(new Set());
        }
    };

    useEffect(() => {
        if (field.LovAPI) {
            setCurrentPage(0);
            if (!isPaginationEnabled || isClientSide) {
                fetchData(0);
            }
        } else {
            setIsLoading(false);
        }
    }, [field.LovAPI, field.DataPath, field.DynamicAccordionFormConfig, fetchData]);

    useEffect(() => {
        if (field.LovAPI && isPaginationEnabled && !isClientSide) {
            fetchData(currentPage);
        }
    }, [currentPage, field.LovAPI, field.DataPath, field.DynamicAccordionFormConfig]);

    const currentPageItems = useMemo(() => {
        if (!isPaginationEnabled || !isClientSide) return accordionItems;
        const start = currentPage * pageSize;
        return accordionItems.slice(start, start + pageSize);
    }, [isPaginationEnabled, isClientSide, accordionItems, currentPage, pageSize]);

    const renderPagination = (): JSX.Element | null => {
        if (!isPaginationEnabled) return null;

        if (field.IsLastPath) {
            const hasPrev = currentPage > 0;
            const hasNext = !isLastPage;
            if (!hasPrev && !hasNext) return null;
            return (
                <nav className="paged-summary-list__pagination" aria-label="Accordion pagination">
                    {hasPrev && (
                        <button
                            className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                            aria-label="Go to previous page"
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            {isEng ? 'Previous page' : 'Page précédente'}
                        </button>
                    )}
                    {hasNext && (
                        <button
                            className="ontario-button ontario-button--primary paged-summary-list__pagination-next"
                            aria-label="Go to next page"
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            {isEng ? 'Next page' : 'Page suivante'}
                        </button>
                    )}
                </nav>
            );
        }

        if (totalPages <= 1) return null;

        const buttons: JSX.Element[] = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        if (currentPage < totalPages - 1) {
            buttons.push(
                <button
                    key="next"
                    className="ontario-button ontario-button--primary paged-summary-list__pagination-next"
                    aria-label="Go to next page"
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    {isEng ? 'Next page' : 'Page suivante'}
                </button>
            );
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`ontario-button paged-summary-list__pagination-page ${i === currentPage ? 'ontario-button--primary' : 'ontario-button--secondary'}`}
                    onClick={() => handlePageChange(i)}
                    aria-current={i === currentPage ? 'page' : undefined}
                    aria-label={i === currentPage ? `Page ${currentPage + 1}, current page` : `Go to page ${i + 1}`}
                >
                    {i + 1}
                </button>
            );
        }

        if (endPage < totalPages - 1) {
            buttons.push(
                <span key="ellipsis" className="paged-summary-list__pagination-ellipsis" aria-hidden="true">...</span>
            );
            buttons.push(
                <button
                    key={totalPages - 1}
                    className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                    aria-label={`Go to page ${totalPages}`}
                    onClick={() => handlePageChange(totalPages - 1)}
                >
                    {totalPages}
                </button>
            );
        }

        if (currentPage > 0) {
            buttons.push(
                <button
                    key="prev"
                    className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                    aria-label="Go to previous page"
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    {isEng ? 'Previous page' : 'Page précédente'}
                </button>
            );
        }

        return (
            <nav className="paged-summary-list__pagination" aria-label="Accordion pagination">
                {buttons}
            </nav>
        );
    };

    if (field.Visible === false) {
        return null;
    }

    if (isLoading) {
        return <p>{isEng ? 'Loading...' : 'Chargement...'}</p>;
    }

    if (!accordionItems.length) {
        return <p>{isEng ? 'No data available' : 'Aucune donnée disponible'}</p>;
    }

    const renderEntryForm = (entry: any, entryIndex: number, accordionIndex: number) => {
        const currentConfig = config;

        if (!currentConfig || !currentConfig.formFields || currentConfig.formFields.length === 0) {
            return null;
        }

        const entryKey = `${field.Id}_accordion_${accordionIndex}_entry_${entryIndex}`;
        let formElements = formElementsCache.get(entryKey);

        if (!formElements) {
            formElements = createFormElementsForEntry(entry, entryIndex, accordionItems[accordionIndex].data, accordionIndex, currentConfig);
            formElements.forEach((element, idx) => {
                if (currentConfig && currentConfig.formFields[idx]) {
                    const fieldDef = currentConfig.formFields[idx];
                    const currentEntryValue = entry[fieldDef.bindName];

                    if (fieldDef.type === 'Date') {
                        if (currentEntryValue !== undefined && currentEntryValue !== null && currentEntryValue !== '') {
                            const parsedDate = parseDateValue(currentEntryValue);
                            element.Value = parsedDate || currentEntryValue;

                            if (parsedDate && /^\d{2}\/\d{2}\/\d{4}$/.test(parsedDate)) {
                                const [month, day, year] = parsedDate.split('/');
                                element.month = month;
                                element.day = day;
                                element.year = year;
                            }
                        } else {
                            element.Value = '';
                            element.month = '';
                            element.day = '';
                            element.year = '';
                        }
                    } else if (fieldDef.type === 'Dropdown') {
                        if (currentEntryValue !== undefined && currentEntryValue !== null && currentEntryValue !== '') {
                            if (element.ListOfLOVValues && element.ListOfLOVValues.length > 0) {
                                const matchingOption = element.ListOfLOVValues.find(lov =>
                                    lov.code === currentEntryValue ||
                                    lov.description === currentEntryValue ||
                                    lov.code?.toString() === currentEntryValue?.toString()
                                );
                                element.Value = matchingOption ? matchingOption.code : '';
                            } else {
                                element.Value = currentEntryValue;
                            }
                        } else {
                            element.Value = '';
                        }
                    } else if (fieldDef.type === 'MultipleChoice') {
                        if (currentEntryValue !== undefined && currentEntryValue !== null && currentEntryValue !== '') {
                            let selectedValues: string[] = [];

                            if (Array.isArray(currentEntryValue)) {
                                selectedValues = currentEntryValue.map(v => String(v));
                            } else if (typeof currentEntryValue === 'string') {
                                selectedValues = currentEntryValue.includes(';') ? currentEntryValue.split(';') :
                                               currentEntryValue.includes(',') ? currentEntryValue.split(',') :
                                               [currentEntryValue];
                            }

                            const getDelimiter = () => getRootComponent(field)?.ToSanitize ? '|' : '#';
                            const delimiter = getDelimiter();

                            if (element.ListOfLOVValues && element.ListOfLOVValues.length > 0 && selectedValues.length > 0) {
                                const matchedCodes: string[] = [];
                                selectedValues.forEach(value => {
                                    const trimmedValue = String(value).trim();
                                    const matchingOption = element.ListOfLOVValues.find(lov =>
                                        lov.code === trimmedValue ||
                                        lov.description === trimmedValue ||
                                        lov.code?.toString() === trimmedValue
                                    );
                                    if (matchingOption && !matchedCodes.includes(matchingOption.code)) {
                                        matchedCodes.push(matchingOption.code);
                                    }
                                });
                                element.Value = matchedCodes.length > 0 ? matchedCodes.join(delimiter) : '';
                            } else {
                                element.Value = selectedValues.join(delimiter);
                            }
                        } else {
                            element.Value = '';
                        }
                    } else {
                        element.Value = currentEntryValue !== undefined && currentEntryValue !== null ? currentEntryValue : '';
                    }
                }
            });

            const newCache = new Map(formElementsCache);
            newCache.set(entryKey, formElements);
            setFormElementsCache(newCache);
        } else {
            formElements.forEach((element, idx) => {
                if (currentConfig && currentConfig.formFields[idx]) {
                    const fieldDef = currentConfig.formFields[idx];
                    const currentEntryValue = entry[fieldDef.bindName];
                    if (fieldDef.type === 'Date') {
                        if (currentEntryValue && (!element.Value || element.Value === '')) {
                            const parsedDate = parseDateValue(currentEntryValue);
                            if (parsedDate && element.Value !== parsedDate) {
                                element.Value = parsedDate;
                                if (/^\d{2}\/\d{2}\/\d{4}$/.test(parsedDate)) {
                                    const [month, day, year] = parsedDate.split('/');
                                    element.month = month;
                                    element.day = day;
                                    element.year = year;
                                }
                            }
                        }
                    } else if (fieldDef.type === 'Dropdown') {
                        if (currentEntryValue && (!element.Value || element.Value === '')) {
                            if (element.ListOfLOVValues && element.ListOfLOVValues.length > 0) {
                                const matchingOption = element.ListOfLOVValues.find(lov =>
                                    lov.code === currentEntryValue ||
                                    lov.description === currentEntryValue ||
                                    lov.code?.toString() === currentEntryValue?.toString()
                                );
                                element.Value = matchingOption ? matchingOption.code : '';
                            } else {
                                element.Value = currentEntryValue;
                            }
                        }
                    } else if (fieldDef.type === 'MultipleChoice') {
                        if (currentEntryValue && (!element.Value || element.Value === '')) {
                            let selectedValues: string[] = [];

                            if (Array.isArray(currentEntryValue)) {
                                selectedValues = currentEntryValue.map(v => String(v));
                            } else if (typeof currentEntryValue === 'string') {
                                selectedValues = currentEntryValue.includes(';') ? currentEntryValue.split(';') :
                                               currentEntryValue.includes(',') ? currentEntryValue.split(',') :
                                               [currentEntryValue];
                            }

                            const getDelimiter = () => getRootComponent(field)?.ToSanitize ? '|' : '#';
                            const delimiter = getDelimiter();

                            if (element.ListOfLOVValues && element.ListOfLOVValues.length > 0 && selectedValues.length > 0) {
                                const matchedCodes: string[] = [];
                                selectedValues.forEach(value => {
                                    const trimmedValue = String(value).trim();
                                    const matchingOption = element.ListOfLOVValues.find(lov =>
                                        lov.code === trimmedValue ||
                                        lov.description === trimmedValue ||
                                        lov.code?.toString() === trimmedValue
                                    );
                                    if (matchingOption && !matchedCodes.includes(matchingOption.code)) {
                                        matchedCodes.push(matchingOption.code);
                                    }
                                });
                                element.Value = matchedCodes.length > 0 ? matchedCodes.join(delimiter) : '';
                            } else {
                                element.Value = selectedValues.join(delimiter);
                            }
                        }
                    } else {
                        if (currentEntryValue && (!element.Value || element.Value === '')) {
                            element.Value = currentEntryValue;
                        }
                    }
                }
            });
        }

        return (
            <div key={`entry-${accordionIndex}-${entryIndex}`} className="dynamic-form-entry" style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                borderBottom: '1px solid #e0e0e0'
            }}>
                {formElements.map((formElement, elementIndex) => {
                    formElement.parent = field.parent;
                    const fieldDef = currentConfig.formFields[elementIndex];

                    const handleElementChange = (programContainer: ProgramContainer) => {
                        if (fieldDef) {
                            const valueToStore = formElement.Value;
                            entry[fieldDef.bindName] = valueToStore;

                            const updatedItems = [...accordionItems];
                            if (updatedItems[accordionIndex] && updatedItems[accordionIndex].entries[entryIndex]) {
                                updatedItems[accordionIndex].entries[entryIndex][fieldDef.bindName] = valueToStore;
                            }
                            setAccordionItems(updatedItems);
                        }
                        handleFieldChange(programContainer);
                    };

                    if (fieldDef && fieldDef.type === 'ReadOnly') {
                        const label = isEng ? fieldDef.label : (fieldDef.labelFRA || fieldDef.label);
                        return (
                            <div key={`${formElement.Id}_element_${elementIndex}`} style={{ marginBottom: '1rem' }}>
                                <span style={{ fontWeight: 'bold' }}>{label}:</span> {formElement.Value}
                            </div>
                        );
                    }

                    return (
                        <ElementComponent
                            key={`${formElement.Id}_element_${elementIndex}`}
                            isEng={isEng}
                            field={formElement}
                            editable={editable}
                            user={user}
                            topEntity={entry}
                            callbackElement={handleElementChange}
                            callbackStepIndex={callbackStepIndex}
                        />
                    );
                })}
            </div>
        );
    };

    const allExpanded = expandedAccordions.size === accordionItems.length;

    return (
        <div className="dynamic-accordion-form-container ontario-margin-top-24-! ontario-margin-bottom-24-!">
            {accordionItems.length > 1 && (
                <div className="accordion-header" style={{ marginBottom: '1rem' }}>
                    <button
                        className="accordion-button"
                        aria-expanded={allExpanded}
                        onClick={toggleExpandAll}
                        style={{
                            background: 'none',
                            border: '1px solid #ccc',
                            padding: '0.5rem 1rem',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        {allExpanded ? 'Collapse All' : 'Expand all'}
                    </button>
                </div>
            )}
            <div>
                {currentPageItems.map((item) => {
                    const globalIndex = item.accordionIndex;
                    const isExpanded = expandedAccordions.has(globalIndex) || item.expand;
                    return (
                        <div key={`accordion-${globalIndex}`} className="accordion-panel" style={{ marginBottom: '0.5rem' }}>
                            <div className="accordion-title">
                                <button
                                    className="accordion-title-button"
                                    aria-expanded={isExpanded}
                                    aria-controls={`accordion-content-${globalIndex}`}
                                    onClick={() => toggleAccordion(globalIndex)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '1rem',
                                        background: '#f3f3f3',
                                        border: '1px solid #d3d3d3',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {isExpanded ? (
                                            <ChevronUp aria-hidden="true" focusable="false" role="presentation" className='accordion-expand-img' style={{ width: '1rem', height: '1rem' }}/>
                                        ) : (
                                            <ChevronDown aria-hidden="true" focusable="false" role="presentation" className='accordion-expand-img' style={{ width: '1rem', height: '1rem' }}/>
                                        )}
                                        {item.title}
                                    </span>
                                </button>
                            </div>
                            {isExpanded && (
                                <div
                                    id={`accordion-content-${globalIndex}`}
                                    className="accordion-content"
                                    style={{
                                        padding: '1rem',
                                        borderTop: 'none',
                                        backgroundColor: '#fff'
                                    }}
                                >
                                    {item.entries.length > 0 ? (
                                        item.entries.map((entry, entryIndex) =>
                                            renderEntryForm(entry, entryIndex, globalIndex)
                                        )
                                    ) : (
                                        <p>{isEng ? 'No entries available' : 'Aucune entrée disponible'}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {renderPagination()}
        </div>
    );
};

export default DynamicAccordionFormComponent;
